/**
 * feat-107 Slice B — editable working model for the paste importer (Step 2/3).
 *
 * The parser (`notionCurriculumParser.parse`) emits an immutable ParseResult.
 * Step 2 needs an EDITABLE copy the user can correct (pick a missing MC answer,
 * supply a rehearsal Role, override an item's type, move it to another Drill,
 * drop it). This module owns:
 *   - `WorkingDrill` / `WorkingItem` mutable shapes,
 *   - `toWorking()` — ParseResult → working model,
 *   - `computeItemFlags()` — re-derives the Needs-review flags from CURRENT
 *     working state (so a fix clears its flag live), honoring per-item dismissals,
 *   - `unresolvedCount()` / `tally()` — drive the summary bar + Commit gate,
 *   - `buildCommitPlan()` — working model → `CommitPlan` for curriculumCommitService.
 *
 * Owned by frontend-b; not shared with Slice A. Pure functions — unit-tested.
 */

import type {
  CorrectOption,
  QuestionType,
} from '../../../../services/quizContentAdminService';
import type {
  ParseResult,
  ParsedItem,
  ParsedCriterion,
  NeedsReviewFlag,
  NeedsReviewReason,
} from '../../../../services/notionCurriculumParser';
import {
  readingImagesDroppedMessage,
  READING_EMPTY_BODY_MESSAGE,
} from '../../../../services/notionCurriculumParser';
import type {
  CommitPlan,
  CommitTarget,
  CommitDrillInput,
  CommitChildInput,
} from '../../../../services/curriculumCommitService';
import {
  unresolvedRoleMessage,
  ZERO_ROLES_ITEM_MESSAGE,
} from './roleRemapCopy';
import { mcOptionsProblem } from '../../../../utils/mcOptions';

/** The three item types the Step-2 type-override select offers. */
export type ItemType = 'multiple_choice' | 'fill_blank' | 'rehearsal';

/** feat-343: every kind a working item can be. A Reading is NOT offered in the
 *  type-override select (it has no question/rehearsal fields to convert), so it
 *  lives outside `ItemType`. */
export type WorkingItemType = ItemType | 'reading';

/** A–D in working order; index 0..3 → letter a..d. */
export const OPTION_LETTERS: CorrectOption[] = ['a', 'b', 'c', 'd'];

export interface WorkingOption {
  text: string;
  /** True when the parser had to PROPOSE this option's letter (mc_option_no_letter). */
  proposed: boolean;
}

export interface WorkingDrill {
  tempId: string;
  title: string;
  /** Catalog key (LESSON_ICON_CATALOG) or null; importer leaves icon optional. */
  iconKey: string | null;
}

export interface WorkingItem {
  tempId: string;
  drillTempId: string;
  itemType: WorkingItemType;

  // --- question fields (MC + fill) ---
  questionText: string;
  /** Always length 4 for MC AND fill-blank editing; index → OPTION_LETTERS.
   *  feat-109: fill-blank now carries A–D options too (the correct one is the
   *  canonical answer), matching the backend `option_a..d` + `correct_option`. */
  mcOptions: WorkingOption[];
  /** Index 0..3 of the correct option (MC + fill), or null when unpicked. */
  correctIndex: number | null;
  /** Fill-blank EXTRA accepted variants, newline-delimited (API shape). */
  fillVariants: string;
  /** feat-109: wrong-answer explanation (API `explanation`), MC + fill. */
  explanation: string;
  /** feat-109: right-answer praise (API `correct_explanation`), MC + fill. */
  correctExplanation: string;

  // --- rehearsal fields ---
  name: string;
  role: string;
  scenarioText: string;
  /** feat-187: `correct_example_response` — the mobile "Try saying" model
   *  answer. Empty raises a dismissible WARN (the column is nullable). */
  correctExample: string;
  criteria: ParsedCriterion[];

  // --- reading fields (feat-343) ---
  /** Optional Reading title ('' ⇒ committed as null). */
  readingTitle: string;
  /** Normalized markdown body. Empty ⇒ `reading_empty_body` block. */
  readingBody: string;
  /** Images the parser stripped — drives the dismissible `reading_images_dropped` warn. */
  readingImagesDropped: number;

  // --- edit state ---
  dropped: boolean;
  /** User confirmed a proposed-letter option → clears mc_option_no_letter. */
  proposedConfirmed: boolean;
  /** Warn flags the user explicitly dismissed ("looks fine"). */
  dismissed: NeedsReviewReason[];
}

// ============================================================
// ParseResult → working model
// ============================================================

function emptyOptions(): WorkingOption[] {
  return [0, 1, 2, 3].map(() => ({ text: '', proposed: false }));
}

function workingItemFrom(item: ParsedItem): WorkingItem {
  const base: WorkingItem = {
    tempId: item.tempId,
    drillTempId: item.drillTempId,
    itemType:
      item.kind === 'question' ? item.type : item.kind,
    questionText: '',
    mcOptions: emptyOptions(),
    correctIndex: null,
    fillVariants: '',
    explanation: '',
    correctExplanation: '',
    name: '',
    role: '',
    scenarioText: '',
    correctExample: '',
    criteria: [],
    readingTitle: '',
    readingBody: '',
    readingImagesDropped: 0,
    dropped: false,
    proposedConfirmed: false,
    dismissed: [],
  };

  if (item.kind === 'question') {
    base.questionText = item.question_text;
    base.explanation = item.explanation;
    base.correctExplanation = item.correct_explanation;
    // MC + fill both map A–D options into the 4-slot editor (feat-109).
    const opts = emptyOptions();
    item.options.forEach((o) => {
      const idx = OPTION_LETTERS.indexOf(o.letter);
      if (idx >= 0) {
        opts[idx] = { text: o.text, proposed: false };
      }
    });
    if (item.type === 'multiple_choice') {
      // Flag any option the parser marked as a proposed letter.
      const proposedFlag = item.needsReview.some(
        (f) => f.reason === 'mc_option_no_letter',
      );
      if (proposedFlag) {
        // The last filled option is the one we proposed a letter for.
        for (let i = opts.length - 1; i >= 0; i -= 1) {
          if (opts[i].text.trim()) {
            opts[i].proposed = true;
            break;
          }
        }
      }
    } else {
      // fill_blank: extra accepted variants (canonical is the correct option).
      base.fillVariants = item.blankAcceptedVariants ?? '';
    }
    base.mcOptions = opts;
    base.correctIndex =
      item.correct_option != null
        ? OPTION_LETTERS.indexOf(item.correct_option)
        : null;
  } else if (item.kind === 'reading') {
    base.readingTitle = item.title;
    base.readingBody = item.body;
    base.readingImagesDropped = item.imagesDropped;
  } else {
    base.name = item.name;
    base.role = item.role ?? '';
    base.scenarioText = item.scenario_text;
    base.correctExample = item.correct_example_response;
    base.criteria = item.criteria.map((c) => ({ ...c }));
  }

  return base;
}

export interface WorkingModel {
  drills: WorkingDrill[];
  items: WorkingItem[];
}

export function toWorking(result: ParseResult): WorkingModel {
  return {
    drills: result.drills.map((d) => ({ tempId: d.tempId, title: d.title, iconKey: null })),
    items: result.items.map(workingItemFrom),
  };
}

// ============================================================
// Live Needs-review recomputation (ADR 0022 §D4 rule set)
// ============================================================

const FILL_NO_ACCEPT = 'No accepted answers yet — add at least one, or dismiss if the blank is open-ended.';
/** feat-187: same shape as FILL_NO_ACCEPT — a legal write that ships an empty
 *  "Try saying" card, so it warns (dismissible) rather than blocks. */
const REHEARSAL_NO_CORRECT_EXAMPLE =
  'No correct example yet — the trainee sees an empty “Try saying” card. Add a model answer, or dismiss if this rehearsal is open-ended.';

// ============================================================
// feat-248 — role resolution against the tenant's prompt-backed roles
// ============================================================

/**
 * What the wizard currently KNOWS about the tenant's prompt-backed roles.
 *
 * `loading` is not `failed` and neither is "no roles" (§5.6). Between entering
 * Review and the fetch resolving, the role list is UNKNOWN: no unresolved flag
 * may fire and no banner may render, because a banner that flashes
 * "Crew Member isn't a role here" and then vanishes is worse than no banner.
 * `ready` with an EMPTY array is a measurement — this tenant genuinely has no
 * role with an active prompt template — and it blocks.
 */
export type RoleCatalog =
  | { status: 'loading' }
  | { status: 'ready'; roles: string[] }
  | { status: 'failed' };

/** Degrade-open default: role checking off, today's behaviour exactly. Also the
 *  implicit value at every call site that predates feat-248, which is what
 *  keeps the golden tests untouched (AT10). */
export const ROLE_CHECKING_OFF: RoleCatalog = { status: 'failed' };

/**
 * trim → lowercase → collapse internal whitespace → `[\s-]` → `_`.
 *
 * Applied to BOTH sides of the comparison, so it is a canonicalisation and not
 * a one-way cleanup of user input. `Cashier`, `CASHIER`, `  cashier  ` and
 * `crew-member` all land on a single token; `Crew Member` lands on
 * `crew_member`, which correctly does NOT match Grove's only prompt-backed role
 * (`cashier`).
 */
export function normalizeRole(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[\s-]/g, '_');
}

/**
 * The pasted role → the tenant's `role_key`, or `null` when nothing matches.
 *
 * Returns the CATALOG's spelling, never the pasted one — that is what makes the
 * committed payload carry `cashier` for a paste that said `Cashier` (AT5).
 */
export function resolveRole(raw: string, roles: string[]): string | null {
  const target = normalizeRole(raw);
  if (!target) return null;
  return roles.find((r) => normalizeRole(r) === target) ?? null;
}

/** Re-derives the active Needs-review flags from the CURRENT working state. A
 *  dropped item never blocks. Dismissed warn reasons are filtered out.
 *
 *  feat-248: `catalog` is optional and defaults to role-checking-off, so every
 *  pre-existing call site keeps today's behaviour. */
export function computeItemFlags(
  item: WorkingItem,
  catalog: RoleCatalog = ROLE_CHECKING_OFF,
): NeedsReviewFlag[] {
  if (item.dropped) return [];
  const flags: NeedsReviewFlag[] = [];
  const dismissed = (r: NeedsReviewReason) => item.dismissed.includes(r);

  if (item.itemType === 'reading') {
    if (item.readingImagesDropped > 0 && !dismissed('reading_images_dropped')) {
      flags.push({ reason: 'reading_images_dropped', severity: 'warn', message: readingImagesDroppedMessage(item.readingImagesDropped) });
    }
    if (!item.readingBody.trim()) {
      flags.push({ reason: 'reading_empty_body', severity: 'block', message: READING_EMPTY_BODY_MESSAGE });
    }
    return flags;
  }

  if (item.itemType === 'rehearsal') {
    if (!item.name.trim()) {
      flags.push({ reason: 'missing_required_field', severity: 'block', message: 'Rehearsal name is empty — add a short name.' });
    }
    // feat-248 (§5.2, AT9): EMPTY and UNRESOLVED are mutually exclusive by
    // construction — the `else if` is load-bearing. An item can never carry
    // both messages, and the existing missing-role flag is untouched.
    if (!item.role.trim()) {
      flags.push({ reason: 'rehearsal_missing_role', severity: 'block', message: 'No role yet — a role is required (commit fails without it).' });
    } else if (catalog.status === 'ready') {
      if (catalog.roles.length === 0) {
        flags.push({ reason: 'rehearsal_unresolved_role', severity: 'block', message: ZERO_ROLES_ITEM_MESSAGE });
      } else if (resolveRole(item.role, catalog.roles) === null) {
        flags.push({ reason: 'rehearsal_unresolved_role', severity: 'block', message: unresolvedRoleMessage(item.role) });
      }
    }
    if (!item.correctExample.trim() && !dismissed('rehearsal_missing_correct_example')) {
      flags.push({ reason: 'rehearsal_missing_correct_example', severity: 'warn', message: REHEARSAL_NO_CORRECT_EXAMPLE });
    }
    return flags;
  }

  if (!item.questionText.trim()) {
    flags.push({ reason: 'missing_required_field', severity: 'block', message: 'Question text is empty — add the question.' });
  }

  if (item.itemType === 'multiple_choice') {
    const filled = item.mcOptions.filter((o) => o.text.trim().length > 0);
    if (filled.length < 2) {
      flags.push({ reason: 'mc_too_few_options', severity: 'block', message: `Only ${filled.length} option${filled.length === 1 ? '' : 's'} — multiple choice needs at least 2.` });
    }
    // feat-346: 2–4 options, contiguous from A (A, B, D with no C would 422).
    const optionProblem = mcOptionsProblem(item.mcOptions.map((o) => o.text));
    if (optionProblem?.code === 'gap') {
      flags.push({ reason: 'mc_options_gap', severity: 'block', message: optionProblem.message });
    }
    const correctOk =
      item.correctIndex != null && (item.mcOptions[item.correctIndex]?.text.trim().length ?? 0) > 0;
    if (!correctOk) {
      flags.push({ reason: 'mc_no_correct_line', severity: 'block', message: 'No correct answer picked — choose the right option.' });
    }
    const hasUnconfirmedProposed =
      !item.proposedConfirmed &&
      item.mcOptions.some((o) => o.proposed && o.text.trim().length > 0);
    if (hasUnconfirmedProposed) {
      flags.push({ reason: 'mc_option_no_letter', severity: 'block', message: 'One option had no clear A–D letter — confirm the letter we assigned, or fix it.' });
    }
  } else {
    // fill_blank — lenient (feat-109): a warn only when there's no answer at all.
    // The correct A–D option is the canonical answer; variants are extras.
    const hasOption = item.mcOptions.some((o) => o.text.trim().length > 0);
    const hasAccept = hasOption || item.fillVariants.trim().length > 0;
    if (!hasAccept && !dismissed('fill_no_accept')) {
      flags.push({ reason: 'fill_no_accept', severity: 'warn', message: FILL_NO_ACCEPT });
    }
  }

  return flags;
}

/** Total unresolved Needs-review flags across all non-dropped items (Commit gate). */
export function unresolvedCount(
  items: WorkingItem[],
  catalog: RoleCatalog = ROLE_CHECKING_OFF,
): number {
  return items.reduce((n, i) => n + computeItemFlags(i, catalog).length, 0);
}

export interface Tally {
  drills: number;
  mc: number;
  fill: number;
  rehearsal: number;
  /** feat-343 */
  reading: number;
  needsReview: number;
}

export function tally(
  model: WorkingModel,
  catalog: RoleCatalog = ROLE_CHECKING_OFF,
): Tally {
  const live = model.items.filter((i) => !i.dropped);
  const usedDrills = new Set(live.map((i) => i.drillTempId));
  return {
    drills: model.drills.filter((d) => usedDrills.has(d.tempId)).length,
    mc: live.filter((i) => i.itemType === 'multiple_choice').length,
    fill: live.filter((i) => i.itemType === 'fill_blank').length,
    rehearsal: live.filter((i) => i.itemType === 'rehearsal').length,
    reading: live.filter((i) => i.itemType === 'reading').length,
    needsReview: unresolvedCount(live, catalog),
  };
}

// ============================================================
// Working model → CommitPlan
// ============================================================

export interface CommitTargetChoice {
  /** Existing unit id, or '' when creating a new Lesson. */
  unitId: string;
  /** Non-empty → create a new Lesson with this title. */
  newLessonName: string;
}

/**
 * One child's create payload. `sortOrder` is its DOCUMENT-ORDER position within
 * its Drill (feat-187 D8) — 1-based, computed here rather than inferred from
 * completion order, because `commitCurriculum` writes items one at a time and
 * the backend orders questions/rehearsals by `sort_order`.
 */
function childPayload(
  item: WorkingItem,
  sortOrder: number,
  catalog: RoleCatalog,
): CommitChildInput {
  if (item.itemType === 'reading') {
    // feat-343: `tenant_id` is injected by commitCurriculum (it owns the
    // tenant); the plan carries only the content. Empty title → null (the
    // column is nullable and a Reading without a title is legal). `sort_order`
    // is the document position so a Reading written before question 1 sits
    // before it — the backend default is 0 for every type, which would tie.
    const title = item.readingTitle.trim();
    return {
      tempId: item.tempId,
      kind: 'reading',
      label: title || 'Untitled reading',
      payload: {
        title: title || null,
        body: item.readingBody.trim(),
        sort_order: sortOrder,
      },
    };
  }
  const label = (item.itemType === 'rehearsal' ? item.name : item.questionText).trim() || 'Untitled item';
  if (item.itemType === 'rehearsal') {
    // feat-248 (AT5): the payload carries the tenant's `role_key`, never the
    // pasted text. A paste that said `Cashier` commits as `cashier`, because
    // `resolve_prompt_template_version` matches `prompt_template.role` as an
    // EXACT string and `Cashier` would 400 exactly as `Crew Member` did.
    //
    // The `?? item.role.trim()` fallback is the degrade-open path and nothing
    // else: when the roles fetch failed the catalog is `failed`, no unresolved
    // flag ever fired, and the importer is deliberately back to today's
    // behaviour — free text through to the backend, which surfaces its own 400.
    // A resolvable role can never reach this fallback, because an unresolved
    // one is a `block` flag and Commit is gated on zero flags.
    const resolved =
      catalog.status === 'ready' ? resolveRole(item.role, catalog.roles) : null;
    return {
      tempId: item.tempId,
      kind: 'rehearsal',
      label,
      payload: {
        role: resolved ?? item.role.trim(),
        name: item.name.trim(),
        scenario_text: item.scenarioText.trim(),
        // feat-187 (R6): carry the model answer through to createRehearsal,
        // which has always typed it — only the importer dropped it. NULL (not
        // '') when absent, so the column stays honestly empty.
        correct_example_response: item.correctExample.trim() || null,
        sort_order: sortOrder,
        criteria: item.criteria,
      },
    };
  }

  const isFill = item.itemType === 'fill_blank';
  const questionType: QuestionType = isFill ? 'fill_blank' : 'multiple_choice';
  // feat-109: MC + fill share the A–D option mapping; the correct option is the
  // canonical answer for fill-blank (backend stores option_a..d + correct_option).
  const optionText = (i: number) => item.mcOptions[i]?.text.trim() ?? '';
  const correct: CorrectOption = OPTION_LETTERS[item.correctIndex ?? 0];

  return {
    tempId: item.tempId,
    kind: 'question',
    label,
    payload: {
      question_type: questionType,
      question_text: item.questionText.trim(),
      option_a: optionText(0),
      option_b: optionText(1),
      option_c: optionText(2),
      option_d: optionText(3),
      correct_option: correct,
      explanation: item.explanation.trim(),
      correct_explanation: item.correctExplanation.trim(),
      blank_accepted_variants: isFill
        ? (item.fillVariants.trim() ? item.fillVariants.trim() : null)
        : null,
      sort_order: sortOrder,
    },
  };
}

/**
 * The curriculum as it exists TODAY, needed to place imported rows (feat-187
 * D7/D8). Structural on purpose — `QuizUnit[]` from the wizard's props satisfies
 * it with no new fetch and no service import.
 */
export interface ExistingCurriculum {
  units: ReadonlyArray<{
    id: string;
    sort_order: number;
    /** The Lesson's Drills — only their sort_order matters.
     *
     *  feat-198: this key was `lessons` and MUST track `QuizUnit`'s child-array
     *  name, because the wizard satisfies this type STRUCTURALLY with a real
     *  `QuizUnit[]` and no import. The optionality below is load-bearing and
     *  DANGEROUS: when the two names disagree the field is merely ABSENT, so
     *  nothing fails to compile and nothing throws — `maxSortOrder` silently
     *  reads 0 and every imported Drill lands at sort_order 1 on top of the
     *  existing ones. A required field would have failed loudly; an optional
     *  one turns a rename mismatch into silent data placement.
     *
     *  Optional so a caller holding a partially-hydrated Lesson can't crash the
     *  commit; an absent list just means "nothing to append after". */
    drills?: ReadonlyArray<{ sort_order: number }>;
  }>;
}

/** Highest `sort_order` in a list, or 0 when it's empty. NOT `length` — after a
 *  reorder or a delete the values are sparse, so `length + 1` collides with an
 *  existing row while `max + 1` never does. */
function maxSortOrder(rows: ReadonlyArray<{ sort_order: number }>): number {
  return rows.reduce((max, r) => (Number.isFinite(r.sort_order) && r.sort_order > max ? r.sort_order : max), 0);
}

/**
 * Build the CommitPlan for the curriculumCommitService. Dropped items are
 * excluded; Drills with no surviving children are omitted (no empty Drill).
 *
 * feat-187 (R7, D7/D8) — every created row is born with a real `sort_order`
 * instead of the API default 0, which used to sort a freshly imported Lesson
 * ABOVE every hand-made one and left its Drills tied at 0 (so the backend's
 * `ORDER BY sort_order, title` tiebreak returned them ALPHABETICALLY rather than
 * in the order they appeared in the pasted doc):
 *   - a NEW Lesson gets `max(existing units' sort_order) + 1` — bottom of the tree;
 *   - Drills get document-order positions, continuing after the target Lesson's
 *     existing Drills when importing into an EXISTING Lesson, so an import
 *     appends and never interleaves into or jumps above what's already there;
 *   - each Drill's children get 1..n in document order.
 * Nothing already in the tree is renumbered.
 */
export function buildCommitPlan(
  model: WorkingModel,
  target: CommitTargetChoice,
  existing: ExistingCurriculum,
  catalog: RoleCatalog = ROLE_CHECKING_OFF,
): CommitPlan {
  const newLessonTitle = target.newLessonName.trim();
  const commitTarget: CommitTarget = newLessonTitle
    ? {
        kind: 'new',
        payload: { title: newLessonTitle, sort_order: maxSortOrder(existing.units) + 1 },
      }
    : { kind: 'existing', unitId: target.unitId };

  // Importing into an existing Lesson appends after its current Drills; a brand
  // new Lesson starts at 1.
  const targetUnit = newLessonTitle
    ? undefined
    : existing.units.find((u) => u.id === target.unitId);
  let drillSortOrder = targetUnit ? maxSortOrder(targetUnit.drills ?? []) : 0;

  const drills: CommitDrillInput[] = [];
  for (const drill of model.drills) {
    const children = model.items
      .filter((i) => i.drillTempId === drill.tempId && !i.dropped)
      // index + 1 = the child's position in the pasted document, within its Drill.
      .map((item, index) => childPayload(item, index + 1, catalog));
    if (children.length === 0) continue;
    drillSortOrder += 1;
    drills.push({
      tempId: drill.tempId,
      label: drill.title.trim() || 'Untitled Drill',
      payload: {
        title: drill.title.trim() || 'Untitled Drill',
        icon_key: drill.iconKey,
        sort_order: drillSortOrder,
      },
      children,
    });
  }

  return { target: commitTarget, drills };
}
